import { getElement, PageStatus, normalizeForSearch, renderSortMenu } from "./generics";
import { sortPosts, renderPost, Post, PostSortField, PostDimension } from "./posts";
import { api } from "./api";
import { openAddPersonDialog, renderAddPersonButton } from "./addLecturer";

// Constants
const searchInput             = getElement<HTMLInputElement>("search-input");
const postList                 = getElement<HTMLDivElement>("post-list");
const statusBar                = getElement<HTMLParagraphElement>("status-bar");
const sortMenuContainer        = getElement<HTMLDivElement>("sort-menu-container");
const addPersonContainer       = getElement<HTMLDivElement>("add-person-container");
const browseInfo               = getElement<HTMLDivElement>("browse-info");
const quickAddButton           = getElement<HTMLButtonElement>("quick-add-person");

const universitySlug: string | null = browseInfo.dataset.universitySlug || null;
const facultySlug: string | null = browseInfo.dataset.facultySlug || null;
// Przełącznik Zajęcia/Egzamin w panelu "Sortuj" (patrz appendDimensionSection niżej) - na razie
// wyłącznie WPiA UW, ten sam warunek co supports_exam_dimensions w post_detail()/browse().
const supportsExamDimensions = browseInfo.dataset.supportsExamDimensions === "1";

// Powyżej tylu osób na liście zakładamy, że kogokolwiek szukasz, raczej już tu jest - duży,
// stały przycisk "Dodaj osobę" obok wyszukiwarki znika (patrz updateAddPersonVisibility niżej),
// zostaje tylko mały "Dodaj" pod wynikami wyszukiwania (patrz render() - pokazuje się właśnie
// wtedy, gdy wyszukiwanie czegoś NIE znalazło, czyli dokładnie wtedy, gdy jest potrzebny).
const MANY_POSTS_THRESHOLD = 35;

const SORT_STORAGE_KEY = "mumin_browse_sort";
const VALID_SORTS: PostSortField[] = ["surname", "name", "rating", "most_comments", "power"];
const SORT_OPTIONS_ZAJECIA: { value: PostSortField, label: string }[] = [
    { value: "most_comments", label: "Najwięcej komentarzy" },
    { value: "name", label: "Imię" },
    { value: "surname", label: "Nazwisko" },
    { value: "rating", label: "Ocena" },
    { value: "power", label: "Moc" },
];
// Odpowiednik SORT_OPTIONS_ZAJECIA wyżej, dla egzaminu (patrz przełącznik Zajęcia/Egzamin -
// dimensionTabContainer) - bez "Moc" (bayesowski wzór nie jest tu potrzebny, patrz ten sam
// wybór w compareView.ts/ORDER_OPTIONS_EGZAMIN).
const SORT_OPTIONS_EGZAMIN: { value: PostSortField, label: string }[] = [
    { value: "most_exam_comments", label: "Najwięcej opinii o egzaminie" },
    { value: "name", label: "Imię" },
    { value: "surname", label: "Nazwisko" },
    { value: "exam_rating", label: "Ocena egzaminu" },
];

function loadSavedSort(): PostSortField {
    try {
        const saved = localStorage.getItem(SORT_STORAGE_KEY);
        if (saved && (VALID_SORTS as string[]).includes(saved)) {
            return saved as PostSortField;
        }
    } catch { /* ignore */ }
    return "most_comments";
}

function saveSort(sortBy: PostSortField): void {
    try {
        localStorage.setItem(SORT_STORAGE_KEY, sortBy);
    } catch { /* ignore */ }
}

interface AppState {
    posts: Post[];
    status: PageStatus;
    error: string;
    sortBy: PostSortField;
    focusedIndex: number;
    visibleCount: number;
}

let state: AppState = {
    posts: [],
    status: "idle",
    error: "",
    sortBy: loadSavedSort(),
    focusedIndex: -1,
    visibleCount: 20,
};

// Przełącznik Zajęcia/Egzamin (patrz dimensionTabContainer wyżej) - nie jest częścią AppState
// (nie resetuje się przy re-renderze/wyszukiwaniu, tylko jawnie przy kliknięciu w zakładkę,
// patrz switchDimension niżej), ten sam wzorzec co activeDimension w compareView.ts/post_detail.ts.
// Nie zapisywane w localStorage - strona zawsze startuje na "zajecia", tak jak reszta widoków.
let activeDimension: PostDimension = "zajecia";

const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
        state.visibleCount += 20; 
        render();
    }
}, { rootMargin: "100px" });


// Functions
function render(): void {
    // Dodane: Odłączenie obserwatora przed ponownym renderowaniem
    observer.disconnect();
    postList.innerHTML = "";

    if (state.status === "loading") {
        statusBar.textContent = "Ładowanie...";
        return;
    }
    
    if (state.status === "error") {
        statusBar.textContent = `Error: ${state.error}`;
        return;
    }

    // Dodane: Lokalne filtrowanie po imieniu lub nazwisku - bez polskich znaków diakrytycznych
    // i niezależnie od kolejności (imię nazwisko lub nazwisko imię), patrz normalizeForSearch.
    const query = normalizeForSearch(searchInput.value);
    let filteredPosts = state.posts;

    // Lista pokazuje WSZYSTKIE osoby faktycznie przypisane do wydziału (patrz PostListView w
    // pages/api/posts.py - to zapytanie i tak nigdy nie zwraca osób z koszyka "Bez przypisanego
    // wydziału"), niezależnie od tego, czy mają już jakiś komentarz - profil bez opinii dostaje
    // neutralne "Brak opinii" zamiast gwiazdek/"Porównaj" (patrz renderPost w posts.ts).
    if (query) {
        filteredPosts = filteredPosts.filter(p => {
            const name = normalizeForSearch(p.name);
            const surname = normalizeForSearch(p.surname);

            return name.includes(query) ||
                surname.includes(query) ||
                `${name} ${surname}`.includes(query) ||
                `${surname} ${name}`.includes(query);
        });
    }

    // Sortowanie (na przefiltrowanych danych) - przy aktywnym wyszukiwaniu zawsze alfabetycznie
    // po imieniu, niezależnie od wybranego trybu sortowania: przy szukaniu konkretnej osoby po
    // imieniu ranking wg liczby komentarzy/oceny nie ma sensu, a bez tego wymuszenia wyniki z
    // takim samym wynikiem sortowania zostają w przypadkowej kolejności, więc np. "Anna" i
    // "Joanna" przeplatają się zamiast być pogrupowane.
    const sorted = sortPosts(filteredPosts, query ? "name" : state.sortBy);
    // Licznik pokazuje się tylko gdy użytkownik faktycznie coś wpisał w wyszukiwarkę - przy
    // przeglądaniu całej listy bez filtrowania sam tekst jest szumem. Przy ZERO wynikach też nic
    // tu nie piszemy - "0 znalezionych wyników" nie mówi nic więcej niż sama pusta lista poniżej
    // (i podpowiedź "Nie znalazłeś wykładowcy?" - patrz niżej), za to brzmi jak błąd/pustka,
    // zamiast zwyczajnie prowadzić dalej do dodania osoby.
    statusBar.textContent = query && sorted.length > 0 ? `${sorted.length} znalezionych wyników` : "";

    // Dodane: Ucięcie wyników do widocznej liczby
    const visiblePosts = sorted.slice(0, state.visibleCount);

    // Nazwa wydziału pod nazwiskiem TYLKO w widoku całej uczelni (brak facultySlug) - patrz
    // renderPost w posts.ts. Na liście pojedynczego wydziału byłoby to zbędne (i tak wiadomo,
    // gdzie się jest).
    const showFacultyName = !facultySlug;
    visiblePosts.forEach((post) => {
        const article = renderPost(post, facultySlug ?? undefined, showFacultyName, activeDimension);
        postList.appendChild(article);
    });


    const reachedEnd = state.visibleCount >= sorted.length;

    // Dodane: Jeżeli zostało coś jeszcze do wyrenderowania, dołączamy znacznik dla scrolla
    if (!reachedEnd) {
        const sentinel = document.createElement("div");
        sentinel.id = "scroll-sentinel";
        sentinel.style.height = "20px";
        postList.appendChild(sentinel);
        observer.observe(sentinel); // Nasłuchujemy, kiedy użytkownik dojedzie do tego elementu
    }

    // Gdy ktoś aktywnie szuka - pod wynikami podpowiedź o dodaniu osoby, której nie ma na liście
    // (bez aktywnego wyszukiwania ten sam formularz jest dostępny z dużego przycisku obok
    // wyszukiwarki, patrz renderAddPersonButton wyżej). Pytanie zostaje w OBU wariantach (patrz
    // large niżej w renderAddPersonButton) - sama duża pigułka bez żadnego kontekstu nad/obok
    // niej wypadała zbyt sucho ("więcej zachęty" - zgłoszenie); .add-lecturer-prompt to zwykły
    // wiersz (flex, bez ramki) z tym samym marginesem co między kartami, więc mieści się od razu
    // pod ostatnim wynikiem, wyrównane do lewej - jak kolejny kafelek, nie osobny blok.
    if (query) {
        const prompt = document.createElement("div");
        prompt.className = "add-lecturer-prompt";
        // Zawsze powiększony wariant (patrz .add-lecturer-prompt-empty w style.css) - najpierw
        // tylko przy zerze wyników (2026-10-19), od 2026-10-23 na prośbę także pod listą wyników,
        // na dole przewijania.
        prompt.classList.add("add-lecturer-prompt-empty");
        const question = document.createElement("p");
        question.className = "add-lecturer-question";
        question.textContent = sorted.length === 0
            ? "Nie ma tu takiej osoby - dodaj ją"
            : "Nie znalazłeś wykładowcy?";
        prompt.appendChild(question);
        postList.appendChild(prompt);
        renderAddPersonButton(
            prompt, universitySlug, facultySlug, () => searchInput.value,
            true, true, searchInput.value.trim(), true,
        );
    } else if (state.status === "success" && state.posts.length === 0) {
        // Prawdziwy pusty wydział (nie: wyszukiwanie bez wyników - patrz gałąź wyżej) - bez tego
        // #post-list zostawał całkiem pusty, jakby coś się nie wczytało, zamiast tłumaczyć,
        // że to normalne i zachęcać do dodania pierwszej osoby.
        const empty = document.createElement("div");
        empty.className = "empty-faculty-state";
        const heading = document.createElement("p");
        heading.className = "empty-faculty-heading";
        heading.textContent = "Nikt jeszcze nie dodał tu żadnej osoby.";
        const sub = document.createElement("p");
        sub.className = "empty-faculty-subtext";
        // Bez własnego przycisku - duży "Dodaj osobę" obok wyszukiwarki (renderAddPersonButton
        // wołane wyżej) jest zawsze widoczny przy tak krótkiej (0-osobowej) liście, patrz
        // MANY_POSTS_THRESHOLD - drugi identyczny przycisk tuż pod nim byłby zbędnym powtórzeniem.
        sub.textContent = "Dodaj ją przyciskiem „Dodaj osobę” powyżej.";
        empty.append(heading, sub);
        postList.appendChild(empty);
    }
}

// Duży przycisk obok wyszukiwarki (patrz renderAddPersonButton niżej) chowamy przy długiej
// liście - patrz MANY_POSTS_THRESHOLD wyżej. Wołane po każdym załadowaniu postów (loadPosts),
// nie tylko raz przy starcie, bo dopiero wtedy znamy state.posts.length.
function updateAddPersonVisibility(): void {
    addPersonContainer.hidden = state.posts.length > MANY_POSTS_THRESHOLD;
    // Okrągły "+" obok Sortuj zastępuje duży przycisk, gdy ten jest schowany - dwa przyciski
    // "dodaj" obok siebie byłyby powtórzeniem.
    quickAddButton.hidden = !addPersonContainer.hidden;
}

// Zmienione: Pobieramy od razu wszystko, zapytanie wysyłane jest tylko raz - wyszukiwarka i
// sortowanie działają lokalnie na już wczytanych danych.
async function loadPosts(): Promise<void> {
    state = { ...state, status: "loading" };
    render();

    const res = await api.getPosts(universitySlug ?? undefined, facultySlug ?? undefined);

    if (!res.ok) {
        state = { ...state, status: "error", error: res.error };
        render();
        return;
    }

    state = { ...state, posts: res.data.posts, status: "success", visibleCount: 20 };
    updateAddPersonVisibility();
    render();
}

// Page

// Dokłada sekcję "Widok" (Zajęcia/Egzamin) do panelu "Sortuj" - na wyraźną prośbę: schowane W
// TYM SAMYM dropdownie, nie jako osobny widżet obok (ten sam wzorzec co "Semestr"/"Rok studiów"
// w subjectBrowser.ts/appendSemesterSection - dokładane do już zbudowanego .sort-menu-dropdown,
// reużywa te same klasy CSS: sort-menu-divider/sort-menu-section-label/sort-menu-semester-row/
// sort-menu-semester-btn, pigułki zamiast sort-menu-option, żeby nie wyglądało jak kolejna opcja
// sortowania). NIE filtruje listy - osoby bez opinii o egzaminie zostają widoczne z "Brak opinii
// o egzaminie" (patrz renderPost w posts.ts), tak samo jak dziś dla zajęć bez żadnej opinii.
function appendDimensionSection(sortMenuElement: HTMLElement): void {
    const dropdown = sortMenuElement.querySelector(".sort-menu-dropdown");
    if (!dropdown) return;

    const divider = document.createElement("hr");
    divider.className = "sort-menu-divider";
    dropdown.appendChild(divider);

    const heading = document.createElement("div");
    heading.className = "sort-menu-section-label";
    heading.textContent = "Widok";
    dropdown.appendChild(heading);

    const row = document.createElement("div");
    row.className = "sort-menu-semester-row";

    const options: { value: PostDimension, label: string }[] = [
        { value: "zajecia", label: "Zajęcia" },
        { value: "egzamin", label: "Egzamin" },
    ];
    const buttons = options.map(opt => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "sort-menu-semester-btn";
        btn.textContent = opt.label;
        btn.classList.toggle("active", activeDimension === opt.value);
        btn.addEventListener("click", () => {
            if (activeDimension === opt.value) return;
            activeDimension = opt.value;
            // Egzamin startuje zawsze od "Najwięcej opinii o egzaminie" - większość wykładowców
            // ma niewiele (albo zero) ocenionych egzaminów, więc sortowanie od razu wg oceny
            // stawiałoby na czele przypadkowe osoby z 1 opinią na 5*. Zajęcia wracają do
            // zapamiętanego sortowania (loadSavedSort) - saveSort niżej zapisuje TYLKO
            // sortowanie zajęć, żeby przełączanie na egzamin i z powrotem go nie nadpisywało.
            const newSortBy: PostSortField = opt.value === "egzamin" ? "most_exam_comments" : loadSavedSort();
            buildSortMenu(newSortBy);
            state = { ...state, sortBy: newSortBy, visibleCount: 20 };
            render();
        });
        row.appendChild(btn);
        return btn;
    });
    dropdown.appendChild(row);
}

// Menu sortowania ma STAŁĄ listę opcji zbudowaną raz przy renderSortMenu (patrz generics.ts) -
// żeby podmienić opcje przy przełączeniu Zajęcia/Egzamin, trzeba zbudować menu na nowo (stąd
// appendDimensionSection wołane przy KAŻDYM buildSortMenu, nie tylko raz na starcie - inaczej
// przełącznik zniknąłby z panelu po pierwszej zmianie sortowania).
let sortMenu: { element: HTMLElement, setValue: (value: PostSortField) => void };
function buildSortMenu(sortBy: PostSortField): void {
    sortMenuContainer.replaceChildren();
    const options = activeDimension === "egzamin" ? SORT_OPTIONS_EGZAMIN : SORT_OPTIONS_ZAJECIA;
    sortMenu = renderSortMenu(options, sortBy, (newSortBy) => {
        if (activeDimension === "zajecia") saveSort(newSortBy);
        state = { ...state, sortBy: newSortBy, visibleCount: 20 };
        render();
    }, "Sortuj");
    if (supportsExamDimensions) appendDimensionSection(sortMenu.element);
    sortMenuContainer.appendChild(sortMenu.element);
}
buildSortMenu(state.sortBy);

// Wyszukiwarka na profilu wykładowcy (zamiast "Wróć") przenosi tu z zapytaniem w ?q= -
// od razu wstawiamy je do pola, żeby wynik był widoczny bez ponownego wpisywania.
const initialQuery = new URLSearchParams(window.location.search).get("q");
if (initialQuery) {
    searchInput.value = initialQuery;
}

// Stały przycisk "Dodaj osobę" obok wyszukiwarki - renderowany RAZ (nie przy każdym render()) -
// patrz addLecturer.ts, dlaczego to teraz jedyny sposób dodania kogoś, kogo nie ma na liście.
// Kontener startuje z atrybutem hidden w browse.html - dopóki loadPosts() nie policzy realnej
// liczby osób (updateAddPersonVisibility), nie wiadomo jeszcze, czy przycisk powinien być
// widoczny (patrz MANY_POSTS_THRESHOLD); bez tego przycisk mignąłby na moment przy każdym
// wczytaniu wydziału z >35 osobami, zanim fetch zdąży go schować (zgłoszenie: "na telefonie
// przycisk Dodaj renderuje się na chwilę i znika").
renderAddPersonButton(
    addPersonContainer, universitySlug, facultySlug,
    () => searchInput.value
);

// "Dodaj wykładowcę" w menu (patrz base.html) - tutaj, na liście wydziału, otwiera od razu ten
// sam formularz co "Dodaj osobę" zamiast prowadzić do wyboru wydziału. ?dodaj=1 - wejście z
// kafelka po wybraniu wydziału w tym trybie (patrz adding_lecturer w landing_tiles, views.py).
quickAddButton.addEventListener("click", () => {
    openAddPersonDialog(universitySlug, facultySlug, searchInput.value);
});

const menuAddLecturer = document.getElementById("site-menu-add-lecturer");
menuAddLecturer?.addEventListener("click", (event) => {
    event.preventDefault();
    document.getElementById("site-menu-panel")?.classList.remove("open");
    openAddPersonDialog(universitySlug, facultySlug, searchInput.value);
});
const pageParams = new URLSearchParams(window.location.search);
if (pageParams.get("dodaj") === "1") {
    pageParams.delete("dodaj");
    const rest = pageParams.toString();
    // Bez parametru w historii - odświeżenie strony nie otwiera formularza drugi raz.
    history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash);
    openAddPersonDialog(universitySlug, facultySlug, "");
}

loadPosts();

// "pageshow" z persisted=true - powrót do tej strony z bfcache (np. przyciskiem "Wstecz" po
// dodaniu opinii na profilu wykładowcy) przywraca zamrożony, nieodświeżony DOM sprzed nawigacji
// bez ponownego uruchomienia skryptu - ta sama sytuacja co przy #compare-bar/przyciskach
// "Porównaj", patrz refreshCompareButtons w posts.ts. Bez tego lista pokazuje stary
// number_of_comments do czasu pełnego przeładowania strony.
window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
        loadPosts();
    }
});

// Zmienione: Nasłuchuje na wejście i filtruje natychmiastowo po stronie klienta
searchInput.addEventListener("input", () => {
    state.visibleCount = 20; // Reset scrolla przy wyszukiwaniu
    render();
});